<template>
  <div class="my-6 leading-normal" v-if="selectedOffice">
    <div class="flex justify-between">
      <h1 class="text-xl mb-2">Referral #{{ id }}</h1>
      <span class="hover:underline cursor-pointer" @click="$emit('close')">
        <i class="fas fa-arrow-left"></i> Other Referrals
      </span>
    </div>

    <loader v-if="form === null" />
    <div v-else>
      <div v-if="form.status_id === 4" class="mb-6">
        Referral declined.
      </div>
      <div v-else-if="form.cancelled_at" class="mb-6">
        Referral cancelled at {{ form.cancelled_at | datetime }}. Reason: {{ getCancelReason(form.cancel_reason_id) }}
      </div>
      <div v-else class="overflow-x-auto my-12">
        <div class="flex w-full">
          <div class="flex-1" v-for="(item, i) in progress" :key="i">
            <div class="flex items-center w-full">
              <div class="border-b-2 flex-grow" :class="i === 0 ? 'border-transparent' : (item.complete ? 'border-green-dark' : 'border-orange-dark')"></div>
              <div class="border-2 rounded-full w-12 h-12 flex items-center justify-center" :class="item.complete ? 'border-green-dark text-white bg-green-dark' : 'border-orange-dark text-orange-dark bg-orange-lighter'">
                <i class="fas" :class="item.complete ? 'fa-check' : 'fa-exclamation-triangle'"></i>
              </div>
              <div class="border-b-2 flex-grow" :class="i === progress.length - 1 ? 'border-transparent' : (progress[i + 1].complete ? 'border-green-dark' : 'border-orange-dark')"></div>
            </div>
            <div class="w-full px-6 text-sm mt-1 text-center">
              {{ item.name }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="form.automated === true" class="px-6 py-3 mb-6 bg-black text-sm text-white">
        <p>This referral is set to receive automatic updates from your CRM. Edits made here may be overwritten by updates from the CRM.</p>
      </div>

      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 px-2">
          <panel class="pt-6 mb-6">
            <div class="flex">
              <div class="flex-grow">
                <h2 class="font-bold text-sm mb-1">Referred By</h2>
                <div v-if="!form.referred_by_office">
                  [Awaiting Assignment]
                </div>
                <div v-else>
                  <p class="mb-1">{{ form.referred_by_office.name }} ({{ form.referred_by_office.address_town }})</p>
                  <p class="text-xs text-gray-600 mb-6">
                    <span class="block">
                      {{ form.referred_by_office.sales_phone }} | <a :href="`mailto:${form.referred_by_office.sales_email}`" class="no-underline hover:underline text-primary">{{ form.referred_by_office.sales_email }}</a>
                    </span>
                    <span v-if="form.referred_by_user" class="block">
                      {{ form.referred_by_user.first_name }} {{ form.referred_by_user.surname }} (<a :href="`mailto:${form.referred_by_user.email}`" class="no-underline hover:underline text-primary">{{ form.referred_by_user.email }}</a>)
                    </span>
                  </p>
                </div>
              </div>
              <img v-if="form.referred_by_office && form.referred_by_office.style" :src="form.referred_by_office.style.logo_url" class="ml-3 max-w-xxs h-16 object-contain" />
            </div>

            <div class="flex">
              <div class="flex-grow">
                <h2 class="font-bold text-sm mb-1">Referred To</h2>
                <div v-if="!form.referred_to_office">
                  [Awaiting Assignment]
                </div>
                <div v-else>
                  <p class="mb-1">{{ form.referred_to_office.name }} ({{ form.referred_to_office.address_town }})</p>
                  <p class="text-xs text-gray-600 mb-6">
                    <span class="block">
                      {{ form.referred_to_office.sales_phone }} | <a :href="`mailto:${form.referred_to_office.sales_email}`" class="no-underline hover:underline text-primary">{{ form.referred_to_office.sales_email }}</a>
                    </span>
                    <span v-if="form.referred_to_user" class="block">
                      {{ form.referred_to_user.first_name }} {{ form.referred_to_user.surname }} (<a :href="`mailto:${form.referred_to_user.email}`" class="no-underline hover:underline text-primary">{{ form.referred_to_user.email }}</a>)
                    </span>
                  </p>
                </div>
              </div>
              <img v-if="form.referred_to_office && form.referred_to_office.style" :src="form.referred_to_office.style.logo_url" class="ml-3 max-w-xxs h-16 object-contain" />
            </div>
          </panel>

          <panel class="mb-6 pt-6" v-if="form.status_id !== 4">
            <div class="mb-3">
              <h2 class="font-bold text-lg mb-1">{{ getReferralType(form.type_id) }}</h2>
            </div>
            <div v-if="displayFullDetails">
              <div class="mb-3">
                <h2 class="font-bold text-sm mb-1">{{ applicantType }}:</h2>
                <div class="text-sm flex flex-wrap">
                  <div class="w-1/2">
                    <span v-if="direction === 'received' && !form.accepted_at">
                      {{ form.vendor_title }} {{ getApplicantFirstName(form.vendor_first_name) }} {{ form.vendor_surname }}
                    </span>
                    <span v-else>
                      {{ form.vendor_title }} {{form.vendor_first_name }}
                      {{ form.vendor_surname }}
                      ({{ form.vendor_phone }})
                    </span>
                  </div>
                  <div class="w-1/2">
                    <span v-if="direction === 'received' && !form.accepted_at">
                      <a :href="`mailto:${getApplicantEmail(form.vendor_email)}`" class="no-underline hover:underline text-primary">{{ getApplicantEmail(form.vendor_email) }}</a>
                    </span>
                    <span v-else>
                      <a :href="`mailto:${form.vendor_email}`" class="no-underline hover:underline text-primary">{{ form.vendor_email }}</a>
                    </span>
                  </div>
                </div>
              </div>
              <div class="mb-3" v-if="form.vendor2_first_name">
                <h2 class="font-bold text-sm mb-1">{{ applicantType }} 2:</h2>
                <div class="text-sm flex flex-wrap">
                  <div class="w-1/2">
                    <span v-if="direction === 'received' && !form.accepted_at">
                      {{ form.vendor_title }} {{ getApplicantFirstName(form.vendor2_first_name) }} {{ form.vendor2_surname }}
                    </span>
                    <span v-else>
                      {{ form.vendor2_title }} {{form.vendor2_first_name }}
                      {{ form.vendor2_surname }}
                      ({{ form.vendor2_phone }})
                    </span>
                  </div>
                  <div class="w-1/2">
                    <span v-if="direction === 'received' && !form.accepted_at">
                      <a :href="`mailto:${getApplicantEmail(form.vendor2_email)}`" class="no-underline hover:underline text-primary">{{ getApplicantEmail(form.vendor2_email) }}</a>
                    </span>
                    <span v-else>
                      <a :href="`mailto:${form.vendor2_email}`" class="no-underline hover:underline text-primary">{{ form.vendor2_email }}</a>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <p class="mb-1">Surname: {{ form.vendor_surname }}</p>
              <p class="mb-1">Phone: {{ form.vendor_phone_restricted }}</p>
              <p class="mb-1" v-if="form.address_postcode">Address: {{ form.address_thoroughfare + ' ' + form.address_line3 + ' ' + form.address_postcode }}
              <p class="mb-1"><strong>Accept the referral to reveal the full {{ applicantType.toLowerCase() }} contact information{{ form.type_id === 1 ? ' and address' : '' }}.</strong></p>
            </div>

            <div class="mb-3">
              <h2 class="font-bold text-sm mb-1">Additional Information:</h2>
              <div class="text-sm flex flex-wrap">
                <div v-if="form.financial_position_type_id && form.type_id === 2">{{ getFinancialPostion(form.financial_position_type_id) }} /&nbsp;</div>
                <div v-if="form.move_reason_id && form.move_reason_id !== 6"> {{ getMoveReason(form.move_reason_id) }} /&nbsp;</div>
                <div v-if="form.move_reason_id === 6"> {{ form.move_reason_other }} /&nbsp;</div>
                <div v-if="form.currently_listed"> Current Property 'on the market'</div>
                <div v-if="!form.currently_listed"> Current Property 'off the market'</div>
              </div>
            </div>

            <div class="mb-3">
              <h2 class="font-bold text-sm mb-1">Lead Source:</h2>
              <p class="text-sm">
                {{ form.fee && form.fee.lead_source ? form.fee.lead_source.name : '[Please Select]' }}
                <span v-if="form.lead_source_url">
                  (<a :href="form.lead_source_url" class="no-underline hover:underline text-primary" target="_blank">{{ form.lead_source_url }}</a>)
                </span>
              </p>
            </div>

            <div class="mb-3" v-if="form.desired_area">
              <h2 class="font-bold text-sm mb-1">Desired Area:</h2>
              <div class="text-sm flex flex-wrap">
                {{ form.desired_area }}
              </div>
            </div>

            <hr class="border-b w-full">

            <div class="mb-3" v-if="form.type_id === 1">
              <h2 class="font-bold text-sm mb-1">Property Details</h2>
            </div>

            <div class="mb-3" v-if="form.type_id === 2">
              <h2 class="font-bold text-sm mb-1">Property Requirements</h2>
            </div>

            <div class="mb-2 flex flex-wrap" v-if="form.budget">
              <div class="text-sm w-1/3">{{ form.type_id === 1 ? 'Approximate Value: ' : 'Budget: ' }}</div>
              <div class="text-sm">{{ form.address_country ? getCurrencySymbol(form.address_country) : 'GBP' }} {{ form.budget | number }}</div>
            </div>

            <div class="mb-2 flex flex-wrap">
              <div class="text-sm w-1/3">Property Type: </div>
              <div class="text-sm">{{ getPropertyType(form.property_type_id) }}</div>
            </div>

            <div class="mb-2 flex flex-wrap">
              <div class="text-sm w-1/3">{{ form.type_id === 1 ? 'Number of Bedrooms: ' : 'Minimum Bedrooms: ' }}</div>
              <div class="text-sm">{{ form.bedrooms }}</div>
            </div>
            <div class="mb-2 flex flex-wrap">
              <div class="text-sm w-1/3">{{ form.type_id === 1 ? 'Address: ' : 'Desired Location: ' }}</div>
              <div class="text-sm" v-if="form.type_id === 2">{{ form.desired_area ? form.desired_area : '[Please Select]' }}</div>
              <div class="text-sm" v-else>{{ address }}</div>
            </div>
            <div class="my-2" v-if="form.notes && !(!form.accepted_at && form.fee && form.fee.lead_source && form.fee.lead_source.uid === 'leadpro')">
              <h2 class="font-bold text-sm mb-1">Agent Notes</h2>
              <p class="text-sm">{{ form.notes }}</p>
            </div>
          </panel>

          <panel v-if="hasTermsInfo" class="mb-6 pt-6">
            <div class="mb-3">
              <h2 class="font-bold text-lg mb-1">Terms &amp; Conditions</h2>
            </div>
            <div v-if="form.referred_by_office_id === selectedOffice.id">
              <p class="mb-1 text-sm" v-if="form.referred_by_terms_acceptor_id && form.referred_by_terms_acceptor">
                <strong>Terms Accepted By:</strong> {{ form.referred_by_terms_acceptor.first_name }} {{ form.referred_by_terms_acceptor.surname }}
              </p>
              <p class="mb-1 text-sm" v-if="form.referred_by_terms_accepted_at">
                <strong>Date:</strong> {{ form.referred_by_terms_accepted_at | datetime }}
              </p>
              <div class="mb-1 text-sm">
                <a class="cursor-pointer hover:underline" @click="termsModalOpen = true">View Terms</a>
                <modal v-if="termsModalOpen" @leave="termsModalOpen = false" show-close-btn>
                  <h2 class="font-bold text-lg mb-4">Terms &amp; Conditions</h2>
                  <div v-html="form.referred_by_terms.content" class="max-h-80 overflow-y-scroll border py-2 px-4 leading-normal page-content text-sm"></div>
                </modal>
              </div>
            </div>
            <div v-if="form.referred_to_office_id === selectedOffice.id">
              <p class="mb-1 text-sm" v-if="form.referred_to_terms_acceptor_id && form.referred_to_terms_acceptor">
                <strong>Terms Accepted By:</strong> {{ form.referred_to_terms_acceptor.first_name }} {{ form.referred_to_terms_acceptor.surname }}
              </p>
              <p class="mb-1 text-sm" v-if="form.referred_to_terms_accepted_at">
                <strong>Date:</strong> {{ form.referred_to_terms_accepted_at | datetime }}
              </p>
              <div class="mb-1 text-sm">
                <a class="cursor-pointer text-primary-600 hover:underline" @click="termsModalOpen = true">View Terms</a>
                <modal v-if="termsModalOpen" @leave="termsModalOpen = false" show-close-btn>
                  <h2 class="font-bold text-lg mb-4">Terms &amp; Conditions</h2>
                  <div v-html="form.referred_to_terms.content" class="max-h-80 overflow-y-scroll border py-2 px-4 leading-normal page-content text-sm"></div>
                </modal>
              </div>
            </div>
          </panel>
        </div>

        <div class="w-full md:w-1/2 px-2">
          <panel v-if="!form.valued_at && form.referred_to_office_id === selectedOffice.id && form.referred_by_office && form.referred_to_office.network_id === 2 && form.type_id === 1 && form.fee && form.fee.lead_source && form.fee.lead_source.uid === 'leadpro'" class="pt-6 mb-6 text-sm">
            <p class="mb-2">{{ form.vendor_title }} {{ form.vendor_surname }} has enquired to view a property that is being sold by {{ form.referred_by_office.name }}, but has a house to sell in your area.</p>
            <p class="mb-2">{{ form.vendor_title }} {{ form.vendor_surname }} has completed a questionnaire, to state that they have a house to sell and they have ticked a box to say that they would like to be contacted by the local Guild agent, for a valuation.</p>
            <p><span v-if="form.accepted_at">Their property address is: {{ [form.address_name_number, form.address_line2, form.address_line3, form.address_town, form.address_line4, form.address_postcode].filter(Boolean).join(', ') }} at which we believe is within your coverage area.</span><span v-else>Their property postcode is: {{ form.address_postcode }} at which we believe is within your coverage area.</span> Please contact {{ form.vendor_title }} {{ form.vendor_surname }} ASAP to arrange a market appraisal.</p>
          </panel>

          <panel v-else-if="!form.valued_at && form.referred_to_office_id === selectedOffice.id && form.referred_by_office && form.referred_to_office.network_id === 2" class="pt-6 mb-6 text-sm">
            <h2 class="font-bold text-sm mb-3">Contacting the {{ applicantType }}</h2>
            <p class="mb-3">This lead has been passed to you via {{ (!form.fee || !form.fee.lead_source || form.fee.lead_source.uid === 'estate-agent') && form.referred_by_office ? form.referred_by_office.name : (form.fee && form.fee.lead_source ? form.fee.lead_source.name : '[Unknown]') }}, therefore when calling the customer please make sure you explain the relationship between your office and the referring Guild Member.</p>
            <p class="mb-3">Below is some suggested wording, which you may use when making the call to the customer:</p>
            <div class="bg-grey-lighter rounded p-3">
              <p class="mb-2">"Good morning/afternoon {{ form.vendor_title }} {{ form.vendor_surname }},</p>
              <p class="mb-2">I am {{ profile.name }}, calling from {{ selectedOffice.name }}. I understand you are interested in {{ form.type_id === 1 ? `selling your property in ${form.address_town}` : `buying property in ${form.desired_area}` }} with {{ form.referred_by_office.name }} in {{ form.referred_by_office.address_town }}.</p>
              <p class="mb-2">We are part of the same network as {{ form.referred_by_office.name }} and they have informed us you may need some help with {{ form.type_id === 1 ? 'selling your home' : `buying property in the area` }}.</p>
              <p>Is this something we can help you with?"</p>
            </div>
          </panel>

          <panel class="pt-6 mb-6 bg-yellow-lightest border border-yellow-dark" v-if="form.cancel_requested">
            <h2 class="font-bold text-sm mb-3">Cancellation Requested</h2>
            A request has been submitted to cancel this referral. Reason: {{ getCancelReason(form.cancel_reason_id) }}
          </panel>

          <panel class="pt-6 mb-6 bg-yellow-lightest border border-yellow-dark" v-else-if="!form.referred_to_office">
            <h2 class="font-bold text-sm mb-3">Progressing This Referral</h2>
            Your referral will be assigned to another agent shortly.
          </panel>

          <panel class="pt-6 mb-6 bg-yellow-lightest border border-yellow-dark" v-else-if="[3, 5, 6, 7, 8, 9].includes(form.status_id) && form.referred_to_office_id === selectedOffice.id">
            <h2 class="font-bold text-sm mb-3">Progressing This Referral</h2>
            <p>
              <span v-if="form.type_id === 1 && form.status_id === 3">To progress this referral, please let us know once the valuation appointment has been booked.</span>
              <span v-else-if="form.type_id === 2 && form.status_id === 3">To progress this referral, please let us know the address of the property the vendor has made an offer on and when it is sold to them.</span>
              <span v-else-if="form.type_id === 1 && form.status_id === 5">To progress this referral, please let us know once the property has been listed.</span>
              <span v-else-if="form.status_id === 6">To progress this referral, please let us know once the property has been sold.</span>
              <span v-else-if="form.status_id === 7">To progress this referral, please let us know once the property has been exchanged.</span>
              <span v-else-if="form.status_id === 8">To progress this referral, please let us know once the sale has been completed.</span>
              <span v-else-if="form.status_id === 9">Payment summary document sent. Invoice sent <span v-if="form.invoice_sent_at">on {{ form.invoice_sent_at | date }}</span> by our accounts team to the selling agent with the usual payment terms of 30 days. Once paid, referral credit will be sent to the referring agent and/or any appropriate third parties in line with our Referral System T&amp;C's.</span>
            </p>
            <p v-if="[5, 6, 7, 8].includes(form.status_id)" class="mt-6">
              Please update any known information below, and click "Save" at the bottom of the page once finished.
            </p>
          </panel>

          <panel class="pt-6 mb-6 bg-yellow-lightest border border-yellow-dark" v-else-if="[2, 3, 5, 6, 7, 8].includes(form.status_id) && form.referred_by_office_id === selectedOffice.id && form.referred_to_office">
            <h2 class="font-bold text-sm mb-3">Progressing This Referral</h2>
            <p>
              <span v-if="form.status_id === 2">{{ form.referred_to_office.name }} is reviewing your referral.</span>
              <span v-else-if="form.type_id === 1 && form.status_id === 3">{{ form.referred_to_office.name }} will update the referral once a valuation appointment has been booked.</span>
              <span v-else-if="form.type_id === 2 && form.status_id === 3">{{ form.referred_to_office.name }} will update the referral once the vendor has made an offer on one of their listings.</span>
              <span v-else-if="form.type_id === 1 && form.status_id === 5">{{ form.referred_to_office.name }} will update the referral once the property has been listed.</span>
              <span v-else-if="form.status_id === 6">{{ form.referred_to_office.name }} will update the referral once the property has been sold.</span>
              <span v-else-if="form.status_id === 7">{{ form.referred_to_office.name }} will update the referral once the property has been exchanged.</span>
              <span v-else-if="form.status_id === 8">{{ form.referred_to_office.name }} will update the referral once the sale has been completed.</span>
              <span v-else-if="form.status_id === 9">The admin team are finalising the referral.</span>
            </p>
          </panel>

          <panel class="pt-6 mb-6 bg-yellow-lightest border border-yellow-dark" v-if="form.is_inter_company_referral">
            <h2 class="font-bold text-sm mb-3">Inter-Company Referral</h2>
            <p class="mb-3">This is an inter-company referral, voiding all fees.</p>
          </panel>
          <panel class="pt-6 mb-6 bg-yellow-lightest border border-yellow-dark" v-else-if="form.referral_fee_override !== null">
            <h2 class="font-bold text-sm mb-3">Custom Referral Fee Agreement</h2>
            <p class="mb-3">A custom agreement between {{ form.referred_by_office.name }} and {{ form.referred_to_office.name }} has been applied to this referral.</p>
            <p>{{ form.referred_by_office.name }} will receive a fee of {{ form.referral_fee_override }}% of the agreed sale price instead of the standard {{ form.fee[form.type_id === 1 ? 'vendor_referral_fee' : 'applicant_referral_fee'] }}% on this referral.</p>

            <div class="flex justify-end mt-6" v-if="form.custom_fee_doc_url">
              <a :href="form.custom_fee_doc_url" target="_blank" class="bg-white border text-black py-2 px-6 text-sm hover:bg-grey-lighter rounded no-underline">
                <i class="fas fa-file-signature"></i> View Agreement
              </a>
            </div>
          </panel>
          <panel class="pt-6 mb-6 bg-yellow-lightest border border-yellow-dark"
                 v-else-if="(form.unconfirmed_referral_fee !== null || form.referral_fee_override_histories.length > 0) && feeType === 0">
            <h2 class="font-bold text-sm mb-3">Referral Fee History</h2>
            <div v-for="(record,index) in form.referral_fee_override_histories" :key="index">
              <div v-if="form.referred_by_office_id === selectedOffice.id">
                <span v-if="record.waiting_to_side_decision">{{ index === 0? 'You = Sent' : 'You = Negotiated' }} @ {{ formatReferralFeeValue(record.value) }}% split: {{record.created_at | datetime}}</span>
                <span v-else>Negotiated @ {{formatReferralFeeValue(record.value)}}% split: {{record.created_at | datetime}}</span>
              </div>
              <div v-else-if="form.referred_to_office_id === selectedOffice.id">
                <span v-if="record.waiting_to_side_decision">{{ index === 0? 'You = Received' : 'Negotiated' }} @ {{formatReferralFeeValue(record.value)}}% split: {{record.created_at | datetime}}</span>
                <span v-else>You Negotiated @ {{formatReferralFeeValue(record.value)}}% split: {{record.created_at | datetime}}</span>
              </div>
            </div>
            <hr class="mt-4">
            <div v-for="(record,index) in form.referral_fee_override_histories" :key="'description'+index" class="my-2">
              <div v-if="index===0 && record.waiting_to_side_decision && form.referred_by_office_id === selectedOffice.id">You sent this referral to {{form.referred_to_office.name}} on the basis of receiving {{formatReferralFeeValue(record.value)}}% of the commission.</div>
              <div v-else-if="index===0 && record.waiting_to_side_decision && form.referred_to_office_id === selectedOffice.id">You have received this referral from {{form.referred_by_office.name}}. They are offering this referral on the basis of receiving {{formatReferralFeeValue(record.value)}}% of the commission.</div>
              <div v-else-if="record.waiting_to_side_decision && form.referred_by_office_id === selectedOffice.id || !record.waiting_to_side_decision && form.referred_to_office_id === selectedOffice.id">You replied by suggesting a referral fee commission split of {{formatReferralFeeValue(record.value)}}%.</div>
              <div v-else-if="!record.waiting_to_side_decision && form.referred_by_office_id === selectedOffice.id || record.waiting_to_side_decision && form.referred_to_office_id === selectedOffice.id">They declined and instead suggested of {{formatReferralFeeValue(record.value)}}% commission split.</div>
            </div>
            <hr class="border-bottom my-4 w-full">
            <div v-if="form.status_id === 15">
              <div v-if="((form.waiting_to_side_decision && form.referred_by_office_id === selectedOffice.id) || (!form.waiting_to_side_decision && form.referred_to_office_id === selectedOffice.id))" class="flex justify-center my-4">
                <span class="font-bold text-lg"><i class="fas fa-clock"> Awaiting Response</i></span>
              </div>
              <div v-else-if="((form.waiting_to_side_decision && form.referred_to_office_id === selectedOffice.id) || (!form.waiting_to_side_decision && form.referred_by_office_id === selectedOffice.id))" class="my-4">
                <div>Please let us know if you would like to accept {{form.unconfirmed_referral_fee}}% commission split, re-negotiate the % or close the referral</div>
                <div class="flex flex-wrap my-6">
                  <span class="w-full">Referral Fee = {{ form.fee[form.type_id === 1 ? 'vendor_referral_fee' : 'applicant_referral_fee'] }}% of the commission:</span>
                  <div class="w-full flex my-4 items-center">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="99.99"
                      v-model.number="form.unconfirmed_referral_fee"
                      @keydown="numbersOnly"
                      :disabled="updatingFee"
                      class="block bg-white border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm w-20" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('unconfirmed_referral_fee') }" />
                    <span class="whitespace-no-wrap ml-2">%</span>
                    <button class="bg-primary text-white py-2 px-6 text-sm hover:bg-grey-dark rounded ml-4" @click="negotiateFee()" v-if="form.unconfirmed_referral_fee !== previousUnconfirmFee">
                      Negotiate Commission
                    </button>
                    <button class="bg-transparent text-white py-2 px-6 text-sm bg-grey-dark rounded ml-4" @click="negotiateFee()" v-else disabled>
                      Negotiate Commission
                    </button>
                  </div>
                  <div class="flex justify-end my-4 mb-6">
                    <button class="bg-transparent text-white py-2 px-6 text-sm bg-red-dark hover:bg-red rounded mr-4" @click="declineModalOpen = true">
                      <i class="fas fa-times"></i> Decline Referral
                    </button>
                    <button class="bg-transparent text-white py-2 px-6 text-sm bg-green-dark hover:bg-green rounded" @click="acceptCustomFee()">
                      <i class="fas fa-check"></i> Accept Commission
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="form.status_id === 14" class="my-4">
              <span class="font-bold text-lg flex flex-wrap justify-center mb-4"><i class="fas fa-times-circle"> Referral Closed</i></span>
              <div class="flex flex-wrap justify-center">Cancel Reason Given: {{getCancelReason(form.cancel_reason_id)}}</div>
            </div>
          </panel>

          <panel class="pt-6 mb-6 bg-yellow-lightest border border-yellow-dark" v-if="form.status_id !== 15 && !form.accepted_at && form.referred_to_office_id === selectedOffice.id && !form.cancelled_at && form.status_id !== 4">
            <h2 class="font-bold text-sm mb-1">New Referral</h2>
            <p class="mb-3">Received: {{ form.sent_at | datetime }}</p>
            <p>You have received this referral{{ form.referred_by_office ? ` from ${form.referred_by_office.name}` : '' }}. Please let us know if you would like to take on the referral, or pass it on to another agent.</p>

            <div class="flex justify-end mt-6">
              <button class="bg-transparent text-white py-2 px-6 text-sm bg-red-dark hover:bg-red rounded" @click="declineModalOpen = true">
                <i class="fas fa-times-circle"></i> Decline Referral
              </button>

              <button class="bg-transparent text-white py-2 px-6 text-sm bg-green-dark hover:bg-green rounded shadow ml-3" @click="acceptModalOpen = true">
                <i class="fas fa-check-circle"></i> Accept Referral
              </button>
            </div>
          </panel>
          <modal v-if="declineModalOpen" @leave="declineModalOpen = false" show-close-btn width="320px">
            <h2 class="font-bold text-sm mb-1">Decline Referral</h2>
            <p class="mb-6">You have chosen to decline this referral and potential revenue - Please select one of the following reasons to help us understand why.</p>

            <label>Reason:</label>
            <select
              v-model="declineForm.reason_id"
              class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('reason_id') }">
              <option :value="null">[Please Select]</option>
              <option :value="1">Office does not cover postcode/area</option>
              <option :value="2">Already exists on office database</option>
              <option :value="3">Duplicate Referral</option>
              <option v-if="[7, 8].includes(form.referred_to_office.network_id)" :value="4">Does not meet Fine &amp; Country criteria</option>
            </select>
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('reason_id')" v-text="errorBag.get('reason_id')"></small>

            <div class="flex justify-end mt-6">
              <button class="bg-transparent text-white py-2 px-6 text-sm bg-red-dark rounded" :class="{'hover:bg-red': !declining}" @click="declineReferral" :disabled="declining">
                <loader v-if="declining" fill-color="white" />
                <span v-else><i class="fas fa-times-circle"></i> Decline Referral</span>
              </button>
            </div>
          </modal>
          <modal v-if="acceptModalOpen" @leave="acceptModalOpen = false" show-close-btn width="320px">
            <h2 class="font-bold text-sm mb-1">Accept Referral</h2>

            <div class="border p-3 h-48 overflow-y-auto leading-normal page-content my-6 text-sm" v-html="terms"></div>

            <div class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 text-sm">
              <input type="checkbox" id="terms" v-model="acceptForm.terms">
              <label for="terms">I accept the terms and conditions</label>
              <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('terms')" v-text="errorBag.get('terms')"></small>
            </div>

            <div class="flex justify-end mt-6">
              <button class="bg-transparent text-white py-2 px-6 text-sm bg-green-dark rounded" :class="{'hover:bg-green': !accepting}" @click="acceptReferral" :disabled="accepting">
                <loader v-if="accepting" fill-color="white" />
                <span v-else><i class="fas fa-check-circle"></i> Accept Referral</span>
              </button>
            </div>
          </modal>

          <panel class="pt-6 mb-6" v-if="form.accepted_at || form.referred_by_office_id === selectedOffice.id && form.status_id !== 15">
            <div class="mb-6">
              <label class="font-bold text-sm mb-3">Referred On:</label>
              <p v-if="form.sent_at">{{ form.sent_at | datetime }}</p>
              <p v-else>TBC</p>
            </div>

            <div class="mb-6">
              <label class="font-bold text-sm mb-3">Accepted On:</label>
              <p v-if="form.accepted_at">{{ form.accepted_at | datetime }}</p>
              <p v-else>TBC</p>
            </div>

            <div v-if="feeType !== 3">
              <div class="mb-6" v-if="form.type_id === 1">
                <label class="font-bold text-sm mb-3">Valuation Date:</label>
                <div v-if="form.referred_by_office_id === selectedOffice.id">
                  <p v-if="form.valued_at">{{ form.valued_at | date }}</p>
                  <p v-else>TBC</p>
                </div>
                <div v-else>
                  <date-time-picker
                    v-model="valuationDate"
                    format="d MMMM yyyy"
                    value-format="yyyy-MM-dd"
                    constraints-format="yyyy-MM-dd"
                    :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get(`valued_at`) ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+((form.accepted_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                    :min-date="valuationDateMin"
                    :max-date="listedDate"
                    :time-picker="false"
                    :today-button="form.listed_at === null"
                    :disabled="form.accepted_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at"
                    auto-close>
                    <template slot="choose-date">
                      [Please Select]
                    </template>
                    <template slot="date">
                      <button class="bg-primary rounded py-2 px-4 text-white">
                        <i class="fas fa-arrow-left"></i> Set Date
                      </button>
                    </template>
                    <template slot="time">
                      <button class="bg-primary rounded py-2 px-4 text-white">
                        Set Time <i class="fas fa-arrow-right"></i>
                      </button>
                    </template>
                    <template slot="today">
                      <button class="bg-grey-light rounded py-2 px-4 text-black" @click="setTime('valued_at')">
                        Set to Today
                      </button>
                    </template>
                  </date-time-picker>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('valued_at')" v-text="errorBag.get('valued_at')"></small>
                </div>
              </div>

              <div :class="{'bg-grey-lighter rounded p-3 mb-6': !form.sole_agency}" v-if="form.type_id === 1">
                <label class="font-bold text-sm mb-3">Sole Agency:</label>
                <div v-if="form.referred_by_office_id === selectedOffice.id">
                  <div class="mb-6">
                    <p v-if="form.sole_agency !== null">{{ form.sole_agency ? 'Sole Agency' : 'Multi-Agency' }}</p>
                    <p v-else>TBC</p>
                  </div>
                  <div v-if="form.sole_agency === false">
                    <div class="mb-6">
                      <label class="font-bold text-sm mb-3">Selling Partner:</label>
                      <p>{{ form.multi_agency_partner }}</p>
                    </div>

                    <div class="mb-6">
                      <label class="font-bold text-sm mb-3">Percentage of the commission {{ form.referred_to_office.name }} will receive upon sale:</label>
                      <p>{{ form.multi_agency_share }}%</p>
                    </div>

                    <div class="mb-6">
                      <label class="font-bold text-sm mb-3">Percentage of the commission {{ form.multi_agency_partner || 'the selling partner' }} will receive upon sale:</label>
                      <p>{{ Math.max(0, Math.min(100, 100 - form.multi_agency_share)) }}%</p>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <div class="mb-6">
                    <select
                      v-model="form.sole_agency"
                      :disabled="form.valued_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at"
                      :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('sole_agency') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+((form.valued_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                      class="block w-full border p-2 mt-2 h-10 text-sm">
                      <option :value="true">Sole Agency</option>
                      <option :value="false">Multi-Agency</option>
                    </select>
                    <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('sole_agency')" v-text="errorBag.get('sole_agency')"></small>
                  </div>

                  <div v-if="!form.sole_agency">
                    <div class="mb-6">
                      <label class="font-bold text-sm mb-3">Selling Partner:</label>
                      <input
                        type="text"
                        v-model="form.multi_agency_partner"
                        class="block w-full border p-2 mt-2 h-10 text-sm"
                        maxlength="64"
                        :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('multi_agency_partner') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')" />
                      <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('multi_agency_partner')" v-text="errorBag.get('multi_agency_partner')"></small>
                    </div>

                    <div class="mb-6">
                      <label class="font-bold text-sm mb-3">Percentage of the commission {{ selectedOffice.name }} will receive upon sale:</label>
                      <input
                        type="number"
                        v-model.number="form.multi_agency_share"
                        pattern="\d*"
                        @keydown="numbersOnly"
                        class="block w-full border p-2 mt-2 h-10 text-sm"
                        min="0"
                        max="100"
                        step="0.01"
                        :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('multi_agency_share') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')" />
                      <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('multi_agency_share')" v-text="errorBag.get('multi_agency_partner')"></small>
                    </div>

                    <div class="mb-6">
                      <label class="font-bold text-sm mb-3">Percentage of the commission {{ form.multi_agency_partner || 'the selling partner' }} will receive upon sale:</label>
                      <p>{{ Math.max(0, Math.min(100, 100 - form.multi_agency_share)) }}%</p>
                    </div>

                    <div class="mb-6">
                      <label class="font-bold text-sm mb-3 block">Proof of Agreement (PDF File):</label>
                      <a v-if="form.multi_agency_doc_url" :href="form.multi_agency_doc_url" target="_blank" class="bg-white border text-black py-2 px-6 text-sm hover:bg-grey-light rounded no-underline cursor-pointer">
                        <i class="fas fa-download"></i> Download Agreement
                      </a>
                      <input v-else type="file" @change="form.multi_agency_doc = $event.target.files[0]" accept="application/pdf" />
                      <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('multi_agency_doc')" v-text="errorBag.get('multi_agency_doc')"></small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex -mx-2" v-if="form.type_id === 1">
                <div class="w-1/2 px-2">
                  <div class="mb-6">
                    <label class="font-bold text-sm mb-3">Listed Date:</label>
                    <div v-if="form.referred_by_office_id === selectedOffice.id">
                      <p v-if="form.listed_at">{{ form.listed_at | date }}</p>
                      <p v-else>TBC</p>
                    </div>
                    <div v-else>
                      <date-time-picker
                        v-model="listedDate"
                        format="d MMMM yyyy"
                        value-format="yyyy-MM-dd"
                        constraints-format="yyyy-MM-dd"
                        :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('listed_at') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+((form.valued_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                        :min-date="valuationDate"
                        :max-date="soldDate"
                        :time-picker="false"
                        :today-button="form.sold_at === null"
                        :disabled="form.valued_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at"
                        auto-close>
                        <template slot="choose-date">
                          [Please Select]
                        </template>
                        <template slot="date">
                          <button class="bg-primary rounded py-2 px-4 text-white">
                            <i class="fas fa-arrow-left"></i> Set Date
                          </button>
                        </template>
                        <template slot="time">
                          <button class="bg-primary rounded py-2 px-4 text-white">
                            Set Time <i class="fas fa-arrow-right"></i>
                          </button>
                        </template>
                        <template slot="today">
                          <button class="bg-grey-light rounded py-2 px-4 text-black" @click="setTime('listed_at')">
                            Set to Today
                          </button>
                        </template>
                      </date-time-picker>
                      <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('listed_at')" v-text="errorBag.get('listed_at')"></small>
                    </div>
                  </div>
                </div>

                <div class="w-1/2 px-2">
                  <div class="mb-6">
                    <label class="font-bold text-sm mb-3">Listed Price ({{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }}):</label>
                    <div v-if="form.referred_by_office_id === selectedOffice.id">
                      <p v-if="form.listed_price">{{ form.listed_price | number }}</p>
                      <p v-else>TBC</p>
                    </div>
                    <div v-else>
                      <input
                        type="text"
                        pattern="\d*"
                        @keydown="numbersOnly"
                        v-model.number="form.listed_price"
                        min="0"
                        class="block w-full border p-2 mt-2 h-10 text-sm"
                        :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('listed_price') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+((form.valued_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                        :disabled="form.valued_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at" />
                      <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('listed_price')" v-text="errorBag.get('listed_price')"></small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mb-6" v-if="form.type_id === 2">
                <div v-if="selectedOffice.network_id !== 8">
                  <div class="flex items-end mt-4">
                    <label class="font-bold text-sm mb-1 flex-grow">Offer Made On:</label>
                    <span @click="propertySelectorOpen = true" class="underline text-grey-dark cursor-pointer hover:text-black text-xs" v-if="form.referred_to_office_id === selectedOffice.id">
                    Select Property
                  </span>
                  </div>
                  <div v-if="form.referred_by_office_id === selectedOffice.id">
                    <p>{{ [form.address_name_number, form.address_line2, form.address_line3, form.address_town, form.address_line4, form.address_postcode].filter(Boolean).join(', ') ? [form.address_name_number, form.address_line2, form.address_line3, form.address_town, form.address_line4, form.address_postcode].filter(Boolean).join(', ') : 'TBC' }}</p>
                  </div>
                  <div v-else>
                    <input
                      v-model="address"
                      class="block w-full border p-2 mt-2 h-10 text-sm"
                      :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('address') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+((form.accepted_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                      :disabled="form.accepted_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at"
                      @click="addressLookupOpen = true" />
                    <!-- <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('sold_commission')" v-text="errorBag.get('sold_commission')"></small> -->
                    <address-lookup-modal
                      v-if="addressLookupOpen"
                      v-model="form"
                      @leave="addressLookupOpen = false"
                    />
                  </div>
                </div>
              </div>

              <div class="flex -mx-2">
                <div class="w-1/2 px-2">
                  <div class="mb-6">
                    <label class="font-bold text-sm mb-3">Sold Date:</label>
                    <div v-if="form.referred_by_office_id === selectedOffice.id">
                      <p v-if="form.sold_at">{{ form.sold_at | date }}</p>
                      <p v-else>TBC</p>
                    </div>
                    <div v-else>
                      <date-time-picker
                        v-model="soldDate"
                        format="d MMMM yyyy"
                        value-format="yyyy-MM-dd"
                        constraints-format="yyyy-MM-dd"
                        :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('sold_at') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+(((form.type_id === 1 ? form.listed_at === null : (form.address_postcode === null && form.desired_area === null)) || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                        :minute-step="5"
                        :min-date="soldDateMin"
                        :max-date="exchangedDate"
                        :time-picker="false"
                        :today-button="form.exchanged_at === null"
                        :disabled="(form.type_id === 1 ? form.listed_at === null : (form.address_postcode === null && form.desired_area === null)) || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at"
                        auto-close>
                        <template slot="choose-date">
                          [Please Select]
                        </template>
                        <template slot="date">
                          <button class="bg-primary rounded py-2 px-4 text-white">
                            <i class="fas fa-arrow-left"></i> Set Date
                          </button>
                        </template>
                        <template slot="today">
                          <button class="bg-grey-light rounded py-2 px-4 text-black" @click="setTime('sold_at')">
                            Set to Today
                          </button>
                        </template>
                      </date-time-picker>
                      <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('sold_at')" v-text="errorBag.get('sold_at')"></small>
                    </div>
                  </div>
                </div>

                <div class="w-1/2 px-2">
                  <div class="mb-6">
                    <label class="font-bold text-sm mb-3">Sold Price ({{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }}):</label>
                    <div v-if="form.referred_by_office_id === selectedOffice.id">
                      <p v-if="form.sold_price">{{ form.sold_price | number }}</p>
                      <p v-else>TBC</p>
                    </div>
                    <div v-else>
                      <input
                        type="text"
                        pattern="\d*"
                        @keydown="numbersOnly"
                        v-model="form.sold_price"
                        class="block w-full border p-2 mt-2 h-10 text-sm"
                        :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('sold_price') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+(((form.type_id === 1 ? form.listed_at === null : (form.address_postcode === null && form.desired_area === null)) || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                        :disabled="(form.type_id === 1 ? form.listed_at === null : (form.address_postcode === null && form.desired_area === null)) || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at" />
                      <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('sold_price')" v-text="errorBag.get('sold_price')"></small>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="feeType === 1" class="rounded bg-grey-lighter p-3 mb-6 text-sm border">
                <p v-if="form.referral_percentage_override !== null">The fee on this referral is based on a split of a custom
                  <span v-if="form.referral_percentage_override_type_id === 1">fixed fee of {{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }}&nbsp;{{ form.referral_percentage_override | number }}.</span>
                  <span v-else>share of {{ form.referral_percentage_override }}% of the agreed sale price.</span>
                </p>
                <p v-else>The fee on this referral is based on a split of the standard share of {{ form.fee[form.type_id === 1 ? 'vendor_fee_percentage' : 'applicant_fee_percentage'] }}% of the agreed sale price.</p>
                <div class="flex justify-end mt-3">
                  <a :href="form.custom_fee_doc_url" target="_blank" v-if="form.custom_fee_doc_url && form.referral_percentage_override" class="bg-white border text-black py-2 px-6 text-sm hover:bg-grey-light rounded no-underline cursor-pointer">
                    <i class="fas fa-download"></i> Download Agreement
                  </a>

                  <button v-if="form.cancelled_at === null && form.completed_at === null" class="bg-white border text-black ml-2 py-2 px-6 text-sm hover:bg-grey-light rounded no-underline" @click="showCustomFeeModal = true">
                    <i class="fas fa-coins"></i> Request Adjustment
                  </button>
                </div>
              </div>
              <div class="flex -mx-2" v-else-if="feeType === 0">
                <div class="w-1/2 px-2">
                  <div class="mb-6">
                    <label class="font-bold text-sm mb-3">Your Commission:</label>
                    <div v-if="form.referred_by_office_id === selectedOffice.id">
                      <p>{{ form.sold_commission ? form.sold_commission : 'TBC' }}</p>
                    </div>
                    <div v-else-if="selectedOffice">
                      <input
                        type="text"
                        pattern="\d*"
                        @keydown="numbersOnly"
                        v-model="form.sold_commission"
                        class="block w-full border p-2 mt-2 h-10 text-sm"
                        :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('sold_commission') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+(((form.type_id === 1 ? form.valued_at === null : form.desired_area === null) || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                        :disabled="(form.type_id === 1 ? form.valued_at === null : form.desired_area === null) || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at" />
                      <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('sold_commission')" v-text="errorBag.get('sold_commission')"></small>
                    </div>
                  </div>
                </div>
                <div class="w-1/2 px-2">
                  <div class="mb-6">
                    <label class="font-bold text-sm mb-3">Commission Unit:</label>
                    <div v-if="form.referred_by_office_id === selectedOffice.id">
                      <p v-if="sold_commission_type_id === 0">% +VAT</p>
                      <p v-if="sold_commission_type_id === 0">% inc. VAT</p>
                      <p v-if="sold_commission_type_id === 0">Fixed Fee ({{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }}) inc. VAT</p>
                      <p v-if="sold_commission_type_id === 0">Fixed Fee ({{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }}) +VAT</p>
                      <p v-else>TBC</p>
                    </div>
                    <div v-else-if="this.selectedOffice.network_id === 8 && selectedOffice.id === form.referred_to_office_id">
                      <span class="block w-full border p-2 mt-2 h-10 text-sm">Fixed Fee ({{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }}) +VAT</span>
                    </div>
                    <div v-else>
                      <select
                        v-model="form.sold_commission_type_id"
                        :disabled="(form.type_id === 1 ? form.valued_at === null : form.desired_area === null) || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at"
                        :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('sold_commission_type_id') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+(((form.type_id === 1 ? form.valued_at === null : form.desired_area === null) || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                        class="block w-full border p-2 mt-2 h-10 text-sm">
                        <option :value="null">[Please Select]</option>
                        <option :value="0">% +VAT</option>
                        <option :value="1">% inc. VAT</option>
                        <option :value="2">Fixed Fee ({{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }}) inc. VAT</option>
                        <option :value="3">Fixed Fee ({{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }}) +VAT</option>
                      </select>
                      <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('sold_commission_type_id')" v-text="errorBag.get('sold_commission_type_id')"></small>
                    </div>

                  </div>
                </div>
              </div>

              <div class="mb-6" v-if="form.address_country === 'GB'">
                <label class="font-bold text-sm mb-3">Exchanged Date:</label>
                <div v-if="form.referred_by_office_id === selectedOffice.id">
                  <p v-if="form.exchanged_at">{{ form.exchanged_at | date }}</p>
                  <p v-else>TBC</p>
                </div>
                <div v-else>
                  <date-time-picker
                    v-model="exchangedDate"
                    format="d MMMM yyyy"
                    value-format="yyyy-MM-dd"
                    constraints-format="yyyy-MM-dd"
                    :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('exchanged_at') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+((form.sold_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                    :min-date="soldDate"
                    :max-date="completionDate"
                    :today-button="form.completed_at === null"
                    :disabled="form.sold_at === null || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at"
                    auto-close>
                    <template slot="choose-date">
                      [Please Select]
                    </template>
                    <template slot="date">
                      <button class="bg-primary rounded py-2 px-4 text-white">
                        <i class="fas fa-arrow-left"></i> Set Date
                      </button>
                    </template>
                    <template slot="today">
                      <button class="bg-grey-light rounded py-2 px-4 text-black" @click="setTime('exchanged_at')">
                        Set to Today
                      </button>
                    </template>
                  </date-time-picker>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('exchanged_at')" v-text="errorBag.get('exchanged_at')"></small>
                </div>
              </div>

              <div class="mb-6">
                <label class="font-bold text-sm mb-3">Completion Date:</label>
                <div v-if="form.referred_by_office_id === selectedOffice.id">
                  <p v-if="form.completed_at">{{ form.completed_at | date }}</p>
                  <p v-else>TBC</p>
                </div>
                <div v-else>
                  <date-time-picker
                    v-model="completionDate"
                    format="d MMMM yyyy"
                    value-format="yyyy-MM-dd"
                    constraints-format="yyyy-MM-dd"
                    :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('completed_at') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')+(((form.address_country === 'GB' ? form.exchanged_at === null : form.sold_at === null) || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at) ? '  bg-grey-lighter' : ' bg-white hover:border-grey focus:border-grey-dark')"
                    :minute-step="5"
                    :min-date="form.address_country === 'GB' ? exchangedDate : soldDate"
                    :time-picker="false"
                    :today-button="form.fee_paid_at === null"
                    :disabled="(form.address_country === 'GB' ? form.exchanged_at === null : form.sold_at === null) || form.cancelled_at || selectedOffice.id !== form.referred_to_office_id || form.fee_paid_at"
                    auto-close>
                    <template slot="choose-date">
                      [Please Select]
                    </template>
                    <template slot="date">
                      <button class="bg-primary rounded py-2 px-4 text-white">
                        <i class="fas fa-arrow-left"></i> Set Date
                      </button>
                    </template>
                    <template slot="time">
                      <button class="bg-primary rounded py-2 px-4 text-white">
                        Set Time <i class="fas fa-arrow-right"></i>
                      </button>
                    </template>
                    <template slot="today">
                      <button class="bg-grey-light rounded py-2 px-4 text-black" @click="setTime('completed_at')">
                        Set to Today
                      </button>
                    </template>
                  </date-time-picker>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('completed_at')" v-text="errorBag.get('completed_at')"></small>
                </div>
              </div>

              <div class="mb-6">
                <div v-if="form.referral_fee && form.completed_at">
                  <div>
                    <strong>Referral Fee:</strong>
                    {{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }} {{ form.referral_fee | number }}
                  </div>
                  <div v-if="form.referred_by_office_id === selectedOffice.id && form.referred_by_management_fee">
                    <strong>Management Fee:</strong>
                    {{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }} {{ form.referred_by_management_fee | number }}
                  </div>
                  <div v-if="form.referred_to_office_id === selectedOffice.id && form.referred_to_management_fee">
                    <strong>Management Fee:</strong>
                    {{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }} {{ form.referred_to_management_fee | number }}
                  </div>
                  <div v-if="form.lead_source_fee">
                    <strong>{{ form.fee && form.fee.lead_source ? form.fee.lead_source.name : 'Lead Source' }} Fee: </strong>
                    {{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }} {{ form.lead_source_fee | number }}
                  </div>
                  <div class="mt-6">
                    <strong>Payable from {{ form.referred_to_office.name }} ({{ form.referred_to_office.address_town }}): </strong>
                    {{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }} {{ form.referral_fee + form.referred_to_management_fee + form.lead_source_fee | number }}
                  </div>
                  <div>
                    <strong>Payable to {{ form.referred_by_office.name }} ({{ form.referred_by_office.address_town }}): </strong>
                    {{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }} {{ form.referral_fee - form.referred_by_management_fee | number }}
                  </div>
                  <div v-if="form.referred_by_office_id === selectedOffice.id && form.referred_by_management_fee">
                    <strong>Payable to {{ form.system.billing_name }}: </strong>
                    {{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }} {{ form.referred_by_management_fee | number }}
                  </div>
                  <div v-if="form.referred_to_office_id === selectedOffice.id && form.total_management_fee">
                    <strong>Payable to {{ form.system.billing_name }}: </strong>
                    {{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }} {{ form.referred_to_management_fee | number }}
                  </div>
                  <div v-if="form.lead_source_fee">
                    <strong>Payable to {{ form.fee && form.fee.lead_source ? form.fee.lead_source.name : 'Lead Source' }}: </strong>
                    {{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }} {{ form.lead_source_fee | number }}
                  </div>
                  <div>
                    <strong>Paid At: </strong>
                    <span v-if="form.fee_paid_at">{{ form.fee_paid_at | datetime }}</span>
                    <span v-else :class="{'text-red-dark': selectedOffice.id === form.referred_to_office_id}">Pending payment</span>
                  </div>
                  <div v-if="form.referred_by_office_id === selectedOffice.id && form.invoice_referred_by_url">
                    <a :href="form.invoice_referred_by_url" target="_blank" class="underline text-xs text-grey-darker hover:text-black">
                      Download Payment Advice
                    </a>
                  </div>
                  <div v-if="form.referred_to_office_id === selectedOffice.id && form.invoice_referred_to_url">
                    <a :href="form.invoice_referred_to_url" target="_blank" class="underline text-xs text-grey-darker hover:text-black">
                      Download Payment Advice
                    </a>
                  </div>
                </div>
                <div v-else class="italic text-grey-dark">
                  <label>Referral Fee:</label>
                  Pending completion
                </div>
              </div>
            </div>

            <div class="flex justify-end" v-if="form.cancelled_at === null && selectedOffice.id === form.referred_to_office_id && form.status_id !== 11">
              <button class="bg-transparent text-white py-2 px-6 text-sm bg-red-dark rounded shadow hover:bg-red mr-3" @click="fallenThroughModalOpen = true" v-if="form.sold_at">
                <i class="fas fa-times-circle mr-1"></i> Sale Fallen Through
              </button>

              <button class="bg-transparent text-white py-2 px-6 text-sm bg-green-dark rounded shadow" :class="{'hover:bg-green': !saving}" @click="save" :disabled="saving">
                <loader v-if="saving" fill-color="white" />
                <span v-else><i class="fas fa-cloud-upload-alt mr-1"></i> Save Changes</span>
              </button>
            </div>

            <modal v-if="fallenThroughModalOpen" @leave="fallenThroughModalOpen = false" show-close-btn width="320px">
              <h2 class="font-bold text-sm mb-1">Sale Fallen Through</h2>
              <p class="mb-6">If you proceed, the referral status will be changed to "Property Listed".</p>

              <div class="flex justify-end">
                <button class="bg-transparent text-primary py-2 px-6 text-sm border border-tertiary rounded shadow hover:bg-tertiary active:bg-primary hover:text-white hover:border-transparent mr-3" @click="fallenThroughModalOpen = false">
                  <i class="fas fa-times-circle mr-1"></i> Cancel
                </button>

                <button class="bg-transparent text-primary py-2 px-6 text-sm border border-tertiary rounded shadow" :class="{'hover:bg-tertiary active:bg-primary hover:text-white hover:border-transparent': !saving}" @click="fallenThrough" :disabled="saving">
                  <loader v-if="saving" />
                  <span v-else><i class="fas fa-check-circle mr-1"></i> Confirm</span>
                </button>
              </div>
            </modal>
          </panel>
        </div>
      </div>

      <panel class="pt-6 mb-6">
        <h2 class="font-bold text-sm mb-3">Referral Updates/Notes Log</h2>
        <loader v-if="notes === null" />
        <div v-else class="flex flex-wrap -mx-3">
          <div class="w-full md:w-2/3 px-3">
            <p v-if="notes.length === 0" class="mb-3">
              There are no notes on this referral.
            </p>
            <div v-else>
              <div v-for="note in notes" :key="note.id">
                <div class="flex -mx-2 border p-3 mb-3" :class="{'bg-yellow-lightest border-yellow-dark rounded': (note.user && note.user.type === 1) || (note.user && note.user.type === 2)}" v-if="note.private_note === true && ((profile.type === 1) || (profile.type === 2) || (profile.id === note.user_id))">
                  <div class="w-20 px-2">
                    <img v-if="note.user && note.user.small_photo_url" :src="note.user.small_photo_url" class="w-16 h-16 rounded-full object-cover" />
                    <div v-else>
                      <div class="w-16 h-16 rounded-full flex items-center justify-center bg-grey-lighter text-grey-dark text-lg">
                        <i class="fas fa-user"></i>
                      </div>
                    </div>
                  </div>
                  <div class="flex-1 px-2">
                    <div class="flex -mx-2">
                      <div class="flex-1 px-2">
                        <div class="flex">
                          <div class="flex-1">
                            <strong v-if="note.user">{{ note.user.first_name }} {{ note.user.surname }}</strong>
                            <strong v-else>System</strong>
                            <span class="bg-blue-dark text-white rounded-full px-2 text-xs ml-2" v-if="[1,2].includes(note.user && note.user.type)">ADMIN</span>
                          </div>

                          <em class="block">{{ note.created_at | datetime }}</em>
                        </div>
                        <div class="italic text-grey-dark text-xs mb-3 mt-1" v-if="note.office || note.notified.length">
                          <span v-if="note.office">
                            From: {{ note.office.name }} ({{ note.office.address_town }}).
                          </span>
                          <span v-if="note.notified.length">
                            Notified: {{ note.notified.join(', ') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p class="whitespace-pre-line">{{ formatNote(note.note) }}</p>
                  </div>
                  <i class="tooltip-container fas fa-eye-slash float-right ml-2">
                    <div class="absolute border border-bg-primary p-2 bg-white shadow z-10 rounded text-xs normal-case mt-1 tooltip-msg pointer-events-none">
                    <span class="text-black font-sans">Only you and Head Office can see this
                    </span>
                    </div>
                  </i>
                </div>
                <div class="flex -mx-2 border p-3 mb-3" :class="{'bg-yellow-lightest border-yellow-dark rounded': (note.user && note.user.type === 1) || (note.user && note.user.type === 2)}" v-else-if="note.private_note === false">
                  <div class="w-20 px-2">
                    <img v-if="note.user && note.user.small_photo_url" :src="note.user.small_photo_url" class="w-16 h-16 rounded-full object-cover" />
                    <div v-else>
                      <div class="w-16 h-16 rounded-full flex items-center justify-center bg-grey-lighter text-grey-dark text-lg">
                        <i class="fas fa-user"></i>
                      </div>
                    </div>
                  </div>
                  <div class="flex-1 px-2">
                    <div class="flex -mx-2">
                      <div class="flex-1 px-2">
                        <div class="flex">
                          <div class="flex-1">
                            <strong v-if="note.user">{{ note.user.first_name }} {{ note.user.surname }}</strong>
                            <strong v-else>System</strong>
                            <span class="bg-blue-dark text-white rounded-full px-2 text-xs ml-2" v-if="[1,2].includes(note.user && note.user.type)">ADMIN</span>
                          </div>

                          <em class="block">{{ note.created_at | datetime }}</em>
                        </div>
                        <div class="italic text-grey-dark text-xs mb-3 mt-1" v-if="note.office || note.notified.length">
                          <span v-if="note.office">
                            From: {{ note.office.name }} ({{ note.office.address_town }}).
                          </span>
                          <span v-if="note.notified.length">
                            Notified: {{ note.notified.join(', ') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p class="whitespace-pre-line">{{ formatNote(note.note) }}</p>
                  </div>
                </div>

              </div>
            </div>
          </div>
          <div class="w-full md:w-1/3 px-3">
            <label>Add Note:</label>
            <textarea
              v-model="noteForm.note"
              rows="4"
              class="block w-full bg-white border hover:border-grey focus:border-grey-dark p-2 mt-2 text-sm"
              :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('note') }"></textarea>
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('note')" v-text="errorBag.get('note')"></small>

            <div class="text-sm mb-6">
              <div v-if="form.referred_by_office && form.referred_to_office" class="mt-4 mb-2">
                <input type="checkbox" id="recipient_office_id" v-model="noteForm.recipient_office_id" :true-value="form.referred_to_office_id === selectedOffice.id ? form.referred_by_office_id : form.referred_to_office_id" :false-value="null" :disabled="noteForm.private_note === true">
                <label for="recipient_office_id" v-html="noteRecipientLabel" style="text-indent: -1.75rem; padding-left: 1.75rem"></label>
                <i class="tooltip-container fas fa-info-circle text-blue ml-2">
                  <div class="absolute border border-bg-primary p-2 bg-white shadow z-10 rounded text-xs normal-case mt-1 tooltip-msg pointer-events-none">
                  <span class="text-black font-sans">Check this tick box to send your note as an email to the referring/receiving agent, as well as adding the note into the system.</span>
                  </div>
                </i>
              </div>
              <div class="mb-2" :class="{ 'mt-4': ! (form.referred_by_office && form.referred_to_office) }">
                <input type="checkbox" id="notify_head_office" v-model="noteForm.notify_head_office">
                <label for="notify_head_office" style="text-indent: -1.75rem; padding-left: 1.75rem">Notify {{ networkName }} Head Office</label>
                <i class="tooltip-container fas fa-info-circle text-blue ml-2">
                  <div class="absolute border border-bg-primary p-2 bg-white shadow z-10 rounded text-xs normal-case mt-1 tooltip-msg pointer-events-none">
                  <span class="text-black font-sans">Check this tick box to send your note as an email to the {{ networkName }} Head Office, as well as adding the note into the system.</span>
                  </div>
                </i>
              </div>
              <div class="mb-2">
                <input type="checkbox" id="private_note" v-model="noteForm.private_note" @click="privateNoteClick()">
                <label for="private_note" style="text-indent: -1.75rem; padding-left: 1.75rem">Private Note</label>
                <i class="tooltip-container fas fa-info-circle text-blue ml-2">
                  <div class="absolute border border-bg-primary p-2 bg-white shadow z-10 rounded text-xs normal-case mt-1 tooltip-msg pointer-events-none">
                  <span class="text-black font-sans">Adds your note to the system as a private note. The referring/receiving agent will not be able to see it, but {{ networkName }} Head Office will.</span>
                  </div>
                </i>
              </div>
            </div>

            <div class="flex justify-end">
              <button class="bg-transparent text-white py-2 px-6 text-sm bg-blue-dark rounded" :class="{'hover:bg-blue': !savingNote}" @click="saveNote" :disabled="savingNote">
                <loader v-if="savingNote" fill-color="white" />
                <span v-else><i class="fas fa-pencil-alt mr-1"></i>Save Note</span>
              </button>
            </div>
          </div>
        </div>
      </panel>

      <div v-if="!form.cancelled_at && !form.cancel_requested && ![4, 11].includes(form.status_id)">
        <div class="flex justify-end mt-6 mb-12">
          <button class="bg-transparent text-white py-2 px-6 text-sm bg-red-dark hover:bg-red rounded" @click="cancelModalOpen = true">
            <i class="fas fa-trash-alt"></i> Cancel Referral
          </button>
        </div>

        <modal v-if="cancelModalOpen" @leave="cancelModalOpen = false" show-close-btn width="320px">
          <h2 class="font-bold text-sm mb-1">Cancel Referral</h2>
          <p class="mb-6">You are requesting to cancel this referral - Please select one of the following reasons to help us understand why.</p>

          <label>Reason:</label>
          <select
            v-model="cancelForm.cancel_reason_id"
            class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('cancel_reason_id') }">
            <option :value="null">[Please Select]</option>
            <option :value="1">No longer moving</option>
            <option :value="2">Proceeding with alternative agent</option>
            <option :value="3">No contact established</option>
            <option :value="4">Admin/User Error</option>
            <option :value="12" v-if="form.referral_system_id===2">Does not meet Fine &amp; Country criteria</option>
          </select>
          <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('cancel_reason_id')" v-text="errorBag.get('cancel_reason_id')"></small>

          <div class="flex justify-end mt-6">
            <button class="bg-transparent text-white py-2 px-6 text-sm bg-red-dark rounded" :class="{'hover:bg-red': !cancelling}" @click="cancelReferral(cancelForm.cancel_reason_id)" :disabled="cancelling">
              <loader v-if="cancelling" fill-color="white" />
              <span v-else><i class="fas fa-trash-alt"></i> Cancel Referral</span>
            </button>
          </div>
        </modal>
      </div>

      <modal v-if="propertySelectorOpen" @leave="propertySelectorOpen = false" show-close-btn width="920px">
          <h2 class="font-bold text-sm mb-1">Select a Property</h2>
          <p class="mb-6">Please select the property the vendor has made an offer on.</p>
          <loader v-if="properties === null" />
          <div v-else class="overflow-x-auto">
            <table class="table-auto text-center w-full">
              <thead class="uppercase text-xs">
                <tr>
                  <th class="text-left p-2 whitespace-no-wrap cursor-pointer" colspan="2">Property</th>
                  <th class="text-left p-2 whitespace-no-wrap cursor-pointer text-center">Price</th>
                  <th class="text-left p-2 whitespace-no-wrap cursor-pointer text-center">Availability</th>
                  <th class="text-left p-2 whitespace-no-wrap cursor-pointer text-center">Uploaded At</th>
                  <th class="text-left p-2 whitespace-no-wrap cursor-pointer text-center">Updated At</th>
                </tr>
              </thead>
              <tbody class="text-sm">
                <tr v-for="(property, i) in properties.data" :key="i"
                  class="border-t border-grey-lighter"
                  :class="property.id === form.property_id ? 'bg-yellow-lighter' : 'hover:bg-grey-lightest cursor-pointer'"
                  @click="selectProperty(property)">
                <td>
                  <div class="icon-img w-24 h-16 my-2 rounded"
                      :style="{'background-image': `url('${property.main_photo}')`}" v-if="property.main_photo"></div>
                  <div v-else class="relative w-24 h-16 px-6 pt-2 my-2 rounded bg-tertiary">
                    <icon name="property_cms" class="text-white"/>
                  </div>
                </td>
                <td class="text-left p-2 font-bold">{{ [property.address_block.address_name_number, property.address_block.address_line2, property.address_block.address_line3, property.address_block.address_town, property.address_block.address_line4, property.address_block.address_postcode].filter(Boolean).join(', ') }}</td>
                <td class="p-2">{{ property.price }}</td>
                <td class="p-2">{{ property.availability.name }}</td>
                <td class="p-2">{{ property.uploaded_at | datetime }}</td>
                <td class="p-2">{{ property.updated_at | datetime }}</td>
              </tr>
              </tbody>
            </table>
            <paginator :data="properties.meta" @change="propertyPage = $event"/>
          </div>
      </modal>

      <modal v-if="showCustomFeeModal" @leave="closeCustomFeeModal" show-close-btn width="320px">
        <h2 class="font-bold text-sm mb-1">Customise Referral Fee</h2>

        <p>
          If you have an agreement with {{ form.referred_to_office_id === selectedOffice.id ? form.referred_by_office.name : form.referred_to_office.name }} to customise the standard {{ form.fee[form.type_id === 1 ? 'vendor_fee_percentage' : 'applicant_fee_percentage'] }}% fee share of the agreed sale price for this referral, please enter the new agreement below and upload a PDF document displaying proof of the agreement.
        </p>

        <div class="flex items-center justify-center my-6 bg-grey-lighter rounded p-3">
          <span>The referral fee share is based on</span>
          <input
            type="number"
            step="0.01"
            min="0"
            max="99.99"
            v-model.number="customFeeForm.referral_percentage_override"
            @keydown="numbersOnly"
            class="block bg-white border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm ml-3 w-32" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('referral_percentage_override') }" />
          <select
            v-model="customFeeForm.referral_percentage_override_type_id"
            class="block bg-white border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm mx-3" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('referral_percentage_override_type_id') }">
            <option :value="0">% of the agreed sale price</option>
            <option :value="1">fixed fee ({{ form.referred_to_office ? form.referred_to_office.currency : 'GBP' }})</option>
          </select>
        </div>
        <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('referral_percentage_override')" v-text="errorBag.get('referral_percentage_override')"></small>

        <p class="mb-2">Please upload proof of this agreement:</p>
        <p v-if="customFeeForm.custom_fee_doc"><span class="text-green-dark"><i class="fas fa-check-circle"></i> Document selected</span> (<span class="hover:underline cursor-pointer" @click="customFeeForm.custom_fee_doc = null">Remove</span>)</p>
        <input v-else type="file" @change="customFeeForm.custom_fee_doc = $event.target.files[0]" accept="application/pdf" />
        <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('custom_fee_doc')" v-text="errorBag.get('custom_fee_doc')"></small>

        <div class="flex justify-end mt-6">
          <button class="text-black py-2 px-6 text-sm bg-white hover:bg-grey-lighter border border-primary rounded shadow" @click="requestCustomFee" :disabled="requestingCustomFees">
            <loader v-if="requestingCustomFees" />
            <span v-else><i class="fas fa-check-circle"></i> Request Change</span>
          </button>
        </div>
      </modal>

      <div v-if="isDirty" class="save-alert fixed pin-b p-3 mb-6 bg-yellow-lightest border border-yellow-dark rounded-lg shadow">
        <i class="fas fa-info-circle"></i> Remember to save your changes before leaving this page.
      </div>
    </div>
  </div>
</template>

<script>
import api, { v2Api } from '@/http'
import ErrorBag from '@/ErrorBag'
import DateTimePicker from 'vue-vanilla-datetime-picker'
import { mapActions, mapState } from 'vuex'
import AddressLookupModal from '../../components/AddressLookupModal.vue'
import moment from 'moment'

export default {
  components: { DateTimePicker, AddressLookupModal },
  props: {
    id: {
      type: Number,
      required: true
    },
    terms: String,
    direction: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      form: null,
      isDirty: false,
      noteForm: {
        recipient_office_id: null,
        note: null,
        private_note: false
      },
      declineForm: {
        reason_id: null
      },
      acceptForm: {
        terms: false
      },
      cancelForm: {
        cancel_reason_id: null
      },
      customFeeForm: {
        custom_fee_doc: null,
        referral_percentage_override: null,
        referral_percentage_override_type_id: 0
      },
      notes: null,
      saving: false,
      savingNote: false,
      updatingFee: false,
      addressLookupOpen: false,
      errorBag: new ErrorBag(),
      declineModalOpen: false,
      acceptModalOpen: false,
      cancelModalOpen: false,
      declining: false,
      accepting: false,
      cancelling: false,
      propertySelectorOpen: false,
      fallenThroughModalOpen: false,
      properties: null,
      propertyPage: 1,
      showCustomFeeModal: false,
      requestingCustomFees: false,
      previousUnconfirmFee: null,
      termsModalOpen: false
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'profile']),
    progress () {
      if (!this.form) {
        return []
      }

      // No Fees
      if (this.feeType === 3) {
        return [
          {
            name: 'Referral Accepted',
            complete: this.form.accepted_at !== null
          },
          {
            name: 'Referral Completed',
            complete: this.form.accepted_at !== null
          }
        ]
      }

      // International Applicant
      if (this.form.address_country !== 'GB' && this.form.type_id === 2) {
        return [
          {
            name: 'Referral Accepted',
            complete: this.form.accepted_at !== null
          },
          {
            name: 'Offer Accepted',
            complete: this.form.sold_at !== null
          },
          {
            name: 'Sale Completed',
            complete: this.form.completed_at !== null
          },
          {
            name: 'Referral Completed',
            complete: this.form.fee_paid_at !== null
          }
        ]
      }

      // Other
      return [
        {
          name: 'Referral Accepted',
          complete: this.form.accepted_at !== null
        },
        {
          name: 'Valuation Booked',
          complete: this.form.valued_at !== null
        },
        {
          name: 'Property Listed',
          complete: this.form.listed_at !== null
        },
        {
          name: 'Property SSTC',
          complete: this.form.sold_at !== null
        },
        this.form.address_country === 'GB' ? {
          name: 'Property Exchanged',
          complete: this.form.exchanged_at !== null
        } : null,
        {
          name: 'Property Completed',
          complete: this.form.completed_at !== null
        },
        {
          name: 'Referral Completed',
          complete: this.form.fee_paid_at !== null
        }
      ].filter(Boolean)
    },
    applicantType () {
      return this.form.type_id === 1 ? 'Vendor' : 'Applicant'
    },
    address () {
      let address = [
        this.form.address_line2,
        this.form.address_line3,
        this.form.address_town,
        this.form.address_line4,
        this.form.address_postcode
      ]

      // Address name / number is not displayed for received referrals which haven't yet been accepted
      if (this.displayFullDetails) {
        address.unshift(this.form.address_name_number)
      }

      address = address.filter(Boolean).join(', ')

      if (!address) {
        return '[Please Select]'
      }

      return `${address}, ${this.form.address_country}`
    },
    networkName () {
      if (this.selectedOffice.network_id === 2) {
        return 'Guild'
      }
      if ([7, 8].includes(this.selectedOffice.network_id)) {
        return 'Fine & Country'
      }
    },
    noteRecipientLabel () {
      if (this.selectedOffice.id === this.form.referred_to_office_id) {
        return 'Notify Referring Agent: ' + this.form.referred_by_office.name
      }

      return 'Notify Referred To Agent: ' + this.form.referred_to_office.name
    },
    valuationDate: {
      get () {
        return this.form && this.form.valued_at ? moment.utc(this.form.valued_at).local().format('YYYY-MM-DD') : null
      },
      set (value) {
        this.form.valued_at = this.dateWithCurrentTime(value)
      }
    },
    listedDate: {
      get () {
        return this.form && this.form.listed_at ? moment.utc(this.form.listed_at).local().format('YYYY-MM-DD') : null
      },
      set (value) {
        this.form.listed_at = this.dateWithCurrentTime(value)
      }
    },
    soldDate: {
      get () {
        return this.form && this.form.sold_at ? moment.utc(this.form.sold_at).local().format('YYYY-MM-DD') : null
      },
      set (value) {
        this.form.sold_at = this.dateWithCurrentTime(value)
      }
    },
    exchangedDate: {
      get () {
        return this.form && this.form.exchanged_at ? moment.utc(this.form.exchanged_at).local().format('YYYY-MM-DD') : null
      },
      set (value) {
        this.form.exchanged_at = this.dateWithCurrentTime(value)
      }
    },
    completionDate: {
      get () {
        return this.form && this.form.completed_at ? moment.utc(this.form.completed_at).local().format('YYYY-MM-DD') : null
      },
      set (value) {
        this.form.completed_at = this.dateWithCurrentTime(value)
      }
    },
    valuationDateMin () {
      return moment.utc(this.form.accepted_at).local().format('YYYY-MM-DD')
    },
    soldDateMin () {
      let minDateTime = this.form.type_id === 1 ? this.form.listed_at : this.form.accepted_at

      return moment.utc(minDateTime).local().format('YYYY-MM-DD')
    },
    displayFullDetails () {
      return this.selectedOffice.id === this.form.referred_by_office_id ||
        (this.selectedOffice.id === this.form.referred_to_office_id && this.form.accepted_at)
    },
    feeType () {
      if (!this.form || !this.form.fee) {
        return null
      }
      if (this.form.is_inter_company_referral) {
        return 3
      }
      return this.form.type_id === 1 ? this.form.fee.vendor_fee_type_id : this.form.fee.applicant_fee_type_id
    },
    hasTermsInfo () {
      if (this.form.referred_by_office_id === this.selectedOffice.id && this.form.referred_by_terms_acceptor) {
        return true
      }

      if (this.form.referred_to_office_id === this.selectedOffice.id && this.form.referred_to_terms_acceptor) {
        return true
      }

      return false
    }
  },
  created () {
    this.getReferral()
    this.getNotes()
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    getReferral () {
      v2Api.get(`referrals/${this.id}`, {
        params: {
          with_relations: 1
        }
      }).then((response) => {
        this.form = response.data
        this.form.multi_agency_doc = null
        // if this is international buyer referral, fix the commission type to 3
        if (this.selectedOffice.network_id === 8 && this.selectedOffice.id === this.form.referred_to_office_id) {
          this.form.sold_commission_type_id = 3
        }
        this.customFeeForm.referral_percentage_override = response.data.referral_percentage_override || response.data.fee[response.data.type_id === 1 ? 'vendor_fee_percentage' : 'applicant_fee_percentage']
        this.customFeeForm.referral_percentage_override_type_id = response.data.referral_percentage_override_type_id || 0
        this.previousUnconfirmFee = response.data.unconfirmed_referral_fee
      })
    },
    closeCustomFeeModal () {
      this.showCustomFeeModal = false
      this.customFeeForm.referral_percentage_override = this.form.referral_percentage_override || this.form.fee[this.form.type_id === 1 ? 'vendor_fee_percentage' : 'applicant_fee_percentage']
      this.customFeeForm.referral_percentage_override_type_id = this.form.referral_percentage_override_type_id || 0
    },
    getNotes () {
      v2Api.get(`referrals/${this.id}/notes`).then((response) => {
        this.notes = response.data
      })
    },
    getCancelReason (id) {
      switch (id) {
        case 1: return 'No longer moving'
        case 2: return 'Proceeding with alternative agent'
        case 3: return 'No contact established'
        case 4: return 'User/Admin error'
        case 5: return 'Already exists on the database'
        case 6: return 'Applicant Referral - Completed'
        case 7: return 'Office does not cover postcode/area'
        case 8: return 'Already exists on office database'
        case 9: return 'Duplicate referral'
        case 10: return 'Unable to allocate'
        case 11: return 'Duplicate lead'
        case 12: return 'Does not meet Fine & Country criteria'
        case 13: return 'Vendor revokes consent'
        case 14: return 'Vendor never gave consent'
        case 15: return 'Commission could not be agreed on'
      }

      return null
    },
    getReferralType (id) {
      switch (id) {
        case 1: return 'Selling referral'
        case 2: return 'Buying Referral'
      }

      return null
    },
    getApplicantEmail (email) {
      if (!email) {
        return null
      }
      const emailArray = email.split('@')
      return emailArray[0].substring(0, 4) + '*****@' + emailArray[1]
    },
    getFinancialPostion (id) {
      switch (id) {
        case 1: return 'Cash Buyer'
        case 2: return 'Finance Required'
        case 3: return 'Unknown'
      }
      return null
    },
    getCurrencySymbol (addressCountry) {
      switch (addressCountry) {
        case 'GB':
        case 'JE':
          return '£'
        case 'AU': return 'A$'
        case 'FR':
        case 'DE':
        case 'HU':
        case 'PT':
        case 'ES':
          return '€'
        case 'MU':
          return 'MUR'
        case 'NA':
          return 'NAD'
        case 'ZA':
          return 'ZAR'
        case 'CH':
          return 'CHF'
        case 'TH':
          return 'THB'
        case 'ZW':
          return '$'
      }

      return null
    },
    getPropertyType (id) {
      switch (id) {
        case 0: return 'Not Specified'
        case 1: return 'Terraced House'
        case 2: return 'End of Terrace House'
        case 3: return 'Semi-Detached House'
        case 4: return 'Detached House'
        case 5: return 'Mews House'
        case 6: return 'Cluster House'
        case 7: return 'Ground Flat'
        case 8: return 'Flat'
        case 9: return 'Studio'
        case 10: return 'Ground Maisonette'
        case 11: return 'Maisonette'
        case 12: return 'Bungalow'
        case 13: return 'Terraced Bungalow'
        case 14: return 'Semi-Detached Bungalow'
        case 15: return 'Detached Bungalow'
        case 16: return 'Mobile Home'
        case 17: return 'Hotel'
        case 18: return 'Guest House'
        case 19: return 'Commercial Property'
        case 20: return 'Land'
        case 21: return 'Link Detached House'
        case 22: return 'Town House'
        case 23: return 'Cottage'
        case 24: return 'Chalet'
        case 25: return 'Character Property'
        case 26: return 'House'
        case 27: return 'Villa'
        case 28: return 'Apartment'
        case 29: return 'Penthouse'
        case 30: return 'Finca'
        case 43: return 'Barn Conversion'
        case 44: return 'Serviced Apartment'
        case 45: return 'Parking'
        case 46: return 'Sheltered Housing'
        case 47: return 'Retirement Property'
        case 48: return 'House Share'
        case 49: return 'Flat Share'
        case 50: return 'Park Home'
        case 51: return 'Garage'
        case 52: return 'Farm House'
        case 53: return 'Equestrian'
        case 56: return 'Duplex'
        case 59: return 'Triplex'
        case 62: return 'Longere'
        case 65: return 'Gite'
        case 68: return 'Barn'
        case 71: return 'Trulli'
        case 74: return 'Mill'
        case 77: return 'Ruins'
        case 80: return 'Restaurant'
        case 83: return 'Café'
        case 86: return 'Mill'
        case 89: return 'Trulli'
        case 92: return 'Castle'
        case 95: return 'Village House'
        case 101: return 'Cave House'
        case 104: return 'Cortijo'
        case 107: return 'Farm Land'
        case 110: return 'Plot'
        case 113: return 'Country House'
        case 116: return 'Stone House'
        case 117: return 'Caravan'
        case 118: return 'Lodge'
        case 119: return 'Log Cabin'
        case 120: return 'Manor House'
        case 121: return 'Stately Home'
        case 125: return 'Off-Plan'
        case 128: return 'Semi-Detached Villa'
        case 131: return 'Detached Villa'
        case 134: return 'Bar'
        case 137: return 'Shop'
        case 140: return 'Riad'
        case 141: return 'House Boat'
        case 142: return 'Hotel Room'
        case 143: return 'Block of Apartments'
        case 144: return 'Private Halls'
        case 178: return 'Office'
        case 181: return 'Business Park'
        case 184: return 'Serviced Office'
        case 187: return 'Retail Property (High Street)'
        case 190: return 'Retail Property (Out of Town)'
        case 193: return 'Convenience Store'
        case 196: return 'Garage'
        case 199: return 'Hairdresser / Barber Shop'
        case 202: return 'Hotel'
        case 205: return 'Petrol Station'
        case 208: return 'Post Office'
        case 211: return 'Pub'
        case 214: return 'Workshop & Retail Space'
        case 217: return 'Distribution Warehouse'
        case 220: return 'Factory'
        case 223: return 'Heavy Industrial'
        case 226: return 'Industrial Park'
        case 229: return 'Light Industrial'
        case 232: return 'Storage'
        case 235: return 'Showroom'
        case 238: return 'Warehouse'
        case 241: return 'Land'
        case 244: return 'Commercial Development'
        case 247: return 'Industrial Development'
        case 250: return 'Residential Development'
        case 253: return 'Commercial Property'
        case 256: return 'Data Centre'
        case 259: return 'Farm'
        case 262: return 'Healthcare Facility'
        case 265: return 'Marine Property'
        case 268: return 'Mixed Use'
        case 271: return 'Research & Development Facility'
        case 274: return 'Science Park'
        case 277: return 'Guest House'
        case 280: return 'Hospitality'
        case 283: return 'Leisure Facility'
        case 284: return 'Beach / Ocean Front'
        case 285: return 'Water Front'
        case 286: return 'Equestrian'
        case 287: return 'Golf'
        case 288: return 'Farms & Ranches'
        case 289: return 'Vacation Rentals'
        case 511: return 'Coach House'
        case 1000: return 'Offices'
        case 1001: return 'Shops'
        case 1002: return 'Industrial/Warehouse'
        case 1003: return 'Office Sharing'
        case 1004: return 'Serviced Offices'
        case 1005: return 'Sectional Title'
        case 1006: return 'Secure Estate'
        case 1007: return 'Secure Golfing Estate'
        case 1008: return 'Secure Wild Life Estate'
        case 1009: return 'Development'
        case 1010: return 'Vacant Land'
        case 1014: return 'Cottage'
        case 1016: return 'Guest Farm'
        case 1018: return 'Winery'
        case 1021: return 'Historic Farm'
        case 1022: return 'Farm - Livestock'
        case 1024: return 'House with additional accommodation'
        case 1025: return 'House - With Land'
        case 1032: return 'Smallholding - 2-50 Acres'
        case 1034: return 'Ground Floor Apartment'
        case 1038: return 'Luxury Apartment'
        case 9998: return 'Game Farms'
        case 9999: return 'Game Lodges'
      }

      return null
    },
    getMoveReason (id) {
      switch (id) {
        case 1: return 'Relocating for work'
        case 2: return 'Relocating for family'
        case 3: return 'Personal reasons'
        case 4: return 'Downsizing'
        case 5: return 'Upsizing'
        case 6: return 'Other'
        case 7: return 'Unknown'
        case 8: return 'Permanent Home'
        case 9: return 'Second Home'
        case 10: return 'Investment Purpose'
      }

      return null
    },
    fallenThrough () {
      this.form.sold_commission = null
      this.form.sold_commission_type_id = null
      this.form.sold_at = null
      this.form.sold_price = null
      this.form.exchanged_at = null
      this.form.completed_at = null

      this.save()
    },
    save () {
      if (this.saving || this.selectedOffice.id !== this.form.referred_to_office_id) {
        return
      }

      this.saving = true
      this.errorBag.clear()

      // if this is international buyer referral, fix the commission type to 3
      if (this.form.sold_commission && this.selectedOffice.network_id === 8 && this.selectedOffice.id === this.form.referred_to_office_id) {
        this.form.sold_commission_type_id = 3
      }

      let formData = new FormData()
      formData.append('_method', 'PATCH')
      formData.append('acting_office_id', this.selectedOffice.id)
      const keys = ['property_id', 'address_name_number', 'address_line2', 'address_line3', 'address_town', 'address_line4', 'address_postcode', 'address_country', 'valued_at', 'sole_agency', 'multi_agency_doc', 'multi_agency_share', 'multi_agency_partner', 'accepted_at', 'listed_at', 'listed_price', 'sold_commission', 'sold_commission_type_id', 'sold_at', 'sold_price', 'exchanged_at', 'completed_at']
      for (let i = 0; i < keys.length; i++) {
        let value = this.form[keys[i]]
        if (value === null) {
          continue
        }
        if (value === true) {
          value = 1
        } else if (value === false) {
          value = 0
        }
        formData.append(keys[i], value)
      }

      v2Api.post(`/referrals/${this.form.id}`, formData).then(
        response => {
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check-circle',
            title: 'Thank You',
            info: 'The referral has been updated.'
          })
          this.form.status_id = response.data.status_id
          this.form.fee = response.data.fee
          this.form.status_updated_at = response.data.status_updated_at
          this.form.referral_fee = response.data.referral_fee
          this.form.referred_by_management_fee = response.data.referred_by_management_fee
          this.form.referred_to_management_fee = response.data.referred_to_management_fee
          this.form.lead_source_fee = response.data.lead_source_fee
          this.form.multi_agency_doc_url = response.data.multi_agency_doc_url
          this.form.multi_agency_doc = null
          this.saving = false
          this.fallenThroughModalOpen = false
          this.getNotes()

          setTimeout(() => {
            this.isDirty = false
          }, 60)
        },
        errors => {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation-triangle',
            title: 'Error',
            info: errors.response.data.message
          })
          this.errorBag.store(errors.response.data)
          this.saving = false
        }
      )
    },
    saveNote () {
      if (this.savingNote) {
        return
      }

      this.savingNote = true
      this.errorBag.clear()
      v2Api.post(`referrals/${this.id}/notes`, {
        office_id: this.selectedOffice.id,
        ...this.noteForm
      }).then((response) => {
        this.notes.unshift(response.data)
        this.savingNote = false
        this.noteForm.note = null
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.savingNote = false
      })
    },
    negotiateFee () {
      if (this.updatingFee) {
        return
      }
      this.updatingFee = true
      v2Api.post(`referrals/${this.id}/negotiate-fee`, {
        waiting_to_side_decision: !this.form.waiting_to_side_decision,
        unconfirmed_referral_fee: this.form.unconfirmed_referral_fee
      }).then((response) => {
        this.form.referral_fee_override_histories.push(response.data)
        this.form.waiting_to_side_decision = !this.form.waiting_to_side_decision
        this.updatingFee = false
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.updatingFee = false
      })
    },
    acceptCustomFee () {
      if (this.updatingFee) {
        return
      }
      this.updatingFee = true
      v2Api.post(`referrals/${this.id}/accept-fee`).then((response) => {
        this.form.waiting_to_side_decision = null
        this.form.unconfirmed_referral_fee = null
        this.form.referral_fee_override = this.form.unconfirmed_referral_fee
        this.form.status_id = response.data.status_id
        this.updatingFee = false
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.updatingFee = false
      })
    },
    declineReferral () {
      if (this.declining) {
        return
      }

      this.declining = true
      this.errorBag.clear()
      v2Api.post(`referrals/${this.id}/rejections`, this.declineForm).then((response) => {
        this.$emit('close')
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Thank You',
          info: 'The referral has been declined.'
        })
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.declining = false
      })
    },
    acceptReferral () {
      if (this.accepting) {
        return
      }

      this.accepting = true
      this.errorBag.clear()
      v2Api.post(`/referrals/${this.form.id}/accept`, this.acceptForm).then(
        response => {
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check-circle',
            title: 'Thank You',
            info: 'The referral has been accepted.'
          })
          this.form = { ...this.form, ...response.data }
          this.accepting = false
          this.acceptModalOpen = false

          setTimeout(() => {
            this.isDirty = false
          }, 60)
        },
        errors => {
          this.errorBag.store(errors.response.data)
          this.accepting = false
        }
      )
    },
    cancelReferral (reasonId = null) {
      if (this.cancelling) {
        return
      }

      // eslint-disable-next-line camelcase
      if (reasonId !== null) {
        this.cancelForm.cancel_reason_id = reasonId
      }

      this.cancelling = true
      this.errorBag.clear()
      v2Api.delete(`/referrals/${this.form.id}`, {
        params: {
          acting_office_id: this.selectedOffice.id,
          ...this.cancelForm
        }
      }).then(
        response => {
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check-circle',
            title: 'Thank You',
            info: 'Cancellation request received.'
          })
          this.form.status_id = response.data.status_id
          this.form.status_updated_at = response.data.status_updated_at
          this.form.cancel_requested = response.data.cancel_requested
          this.form.cancel_reason_id = response.data.cancel_reason_id
          this.form.cancelled_at = response.data.cancelled_at
          this.cancelling = false
          this.cancelModalOpen = false

          setTimeout(() => {
            this.isDirty = false
          }, 60)
        },
        errors => {
          this.errorBag.store(errors.response.data)
          this.cancelling = false
        }
      )
    },
    setTime (key) {
      setTimeout(() => {
        if (!this.form[key]) {
          return
        }
        let now = moment()
        this.form[key] = moment(this.form[key]).hour(now.hour()).minute(now.minute()).format('YYYY-MM-DD HH:mm:ss')
      }, 250)
    },
    numbersOnly (e) {
      if (e.key.length === 1 && e.key !== '.' && /\D/.test(e.key)) {
        e.preventDefault()
      }
    },
    getProperties () {
      api.get('/properties', {
        params: {
          page: this.propertyPage,
          office_id: this.selectedOffice.id,
          department: 'RS',
          order_attribute: 'price',
          order_direction: 'asc'
        }
      }).then((response) => {
        this.properties = response.data
      })
    },
    selectProperty (property) {
      this.form.property_id = property.id
      this.form.address_name_number = property.address_block.address_name_number
      this.form.address_line2 = property.address_block.address_line2
      this.form.address_line3 = property.address_block.address_line3
      this.form.address_town = property.address_block.address_town
      this.form.address_line4 = property.address_block.address_line4
      this.form.address_postcode = property.address_block.address_postcode
      this.form.address_country = property.address_block.address_country
    },
    dateWithCurrentTime (value) {
      let now = moment()
      return moment(value).hour(now.hour()).minute(now.minute()).second(now.second()).format('YYYY-MM-DD HH:mm:ss')
    },
    requestCustomFee () {
      this.requestingCustomFees = true
      this.errorBag.clear()

      let formData = new FormData()
      formData.append('office_id', this.selectedOffice.id)
      for (let key in this.customFeeForm) {
        formData.append(key, this.customFeeForm[key])
      }

      v2Api.post(`/referrals/${this.form.id}/request-custom-fee`, formData).then((response) => {
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Thank You',
          info: 'Your request for altered fees has been received.'
        })
        this.requestingCustomFees = false
        this.notes.unshift(response.data)
        this.showCustomFeeModal = false
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.requestingCustomFees = false
      })
    },
    privateNoteClick () {
      this.noteForm.recipient_office_id = null
    },
    formatNote (note) {
      return note.replace(' by BYM Referrals', '')
    },
    formatReferralFeeValue (number) {
      return number ? number.replace('.00', '') : number
    },
    getApplicantFirstName (firstName) {
      return firstName.replace(/./gi, '*')
    }
  },
  watch: {
    propertySelectorOpen (opened) {
      if (this.properties === null) {
        this.getProperties()
      }
    },
    propertyPage () {
      this.getProperties()
    },
    form: {
      handler (newValue, oldValue) {
        if (!oldValue) {
          return
        }
        this.isDirty = true
      },
      deep: true
    }
  }
}
</script>
<style scoped>
  input[type="checkbox"]:checked + label::before, input[type="radio"]:checked + label::before {
    text-indent: 0;
  }
  .save-alert {
    animation: save-alert .5s
  }
  @keyframes save-alert {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0%);
    }
  }
</style>
